import { call, put, takeEvery, type SagaReturnType } from 'redux-saga/effects'
import {
    getExperiments,
    createExperiment as apiCreateExperiment,
    updateTestOutcome as apiUpdateTestOutcome,
} from 'src/api/voisino/experiments'
import {
    FETCH_EXPERIMENTS,
    CREATE_EXPERIMENT,
    UPDATE_TEST_OUTCOME,
    type CreateExperiment,
    type UpdateTestOutcome,
} from 'src/redux/voisinoExperiments/voisinoExperiments.types'
import { setLoading, setExperiments, fetchExperiments } from 'src/redux/voisinoExperiments/voisinoExperiments.actions'
import { showNotification } from 'src/components/parts/notifications/notifications'

type GetExperimentsRes = SagaReturnType<typeof getExperiments>
type CreateExperimentRes = SagaReturnType<typeof apiCreateExperiment>
type UpdateOutcomeRes = SagaReturnType<typeof apiUpdateTestOutcome>

export function* fetchExperimentsSaga() {
    try {
        yield put(setLoading(true))
        const data: GetExperimentsRes = (yield call(getExperiments)) as GetExperimentsRes
        if (data instanceof Error) {
            throw data
        }
        yield put(setExperiments(data))
    } catch (_) {
        yield call(showNotification, 'error', 'Failed to load experiments')
    } finally {
        yield put(setLoading(false))
    }
}

export function* createExperimentSaga(action: CreateExperiment) {
    try {
        const experiment = action.payload
        const created: CreateExperimentRes = (yield call(apiCreateExperiment, experiment)) as CreateExperimentRes
        if (created instanceof Error) {
            throw created
        }
        yield put(fetchExperiments())
        yield call(showNotification, 'success', 'Experiment created')
    } catch (e: any) {
        const msg = e?.message || 'Unknown error'
        yield call(showNotification, 'error', `Failed to create experiment: ${msg}`)
    }
}

export function* updateOutcomeSaga(action: UpdateTestOutcome) {
    try {
        const { id, outcome, endDate, lifecycleState } = action.payload
        const res: UpdateOutcomeRes = (yield call(
            apiUpdateTestOutcome,
            id,
            outcome,
            endDate,
            lifecycleState,
        )) as UpdateOutcomeRes
        if (res instanceof Error) {
            throw res
        }

        yield put(fetchExperiments())
        yield call(showNotification, 'success', 'Experiment ended')
    } catch (e: any) {
        yield call(showNotification, 'error', e?.message || 'Failed to update experiment')
    }
}

export default function* voisinoExperimentsSagas() {
    yield takeEvery(FETCH_EXPERIMENTS, fetchExperimentsSaga)
    yield takeEvery(CREATE_EXPERIMENT, createExperimentSaga)
    yield takeEvery(UPDATE_TEST_OUTCOME, updateOutcomeSaga)
}
